import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Brand } from './Brand';
import { useApp } from '../context/AppContext';
import { regionFromRuntime, regionShortLabel } from '../lib/regionRuntime';

function isAuthRoute(pathname = '') {
  return ['/signin', '/login', '/auth'].some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

function isRegionRoute(pathname = '') {
  return pathname === '/region' || pathname.startsWith('/region/');
}

function initials(value = '') {
  const clean = String(value || '').trim();
  if (!clean) return 'N';
  return clean.slice(0, 1).toUpperCase();
}

export function AppHeader({ hideAccountActions = false, allowRegionSwitch }) {
  const { state, logout, chooseRegionAgain, setActiveWorkspace } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [accountAnchor, setAccountAnchor] = useState(null);

  const pathname = location.pathname || '';
  const onSigninPage = isAuthRoute(pathname);
  const onRegionPage = isRegionRoute(pathname);
  const runtimeRegion = (() => {
    if (typeof window !== 'undefined') {
      const host = String(window.location.hostname || '').toLowerCase();
      if (host === 'europe.nudgeforme.com' || host === 'europe.nudgefor.me' || host.startsWith('europe.')) return 'eu';
      if (host === 'app.nudgeforme.com' || host === 'app.nudgefor.me' || host.startsWith('app.')) return 'global';
    }
    return regionFromRuntime(state.company?.region || state.region || 'global');
  })();

  const canSwitchRegion = Boolean(allowRegionSwitch ?? onSigninPage);
  const showAccountActions = Boolean(state.user?.email) && !hideAccountActions && !onSigninPage && !onRegionPage;
  const activeWorkspaceId = state.activeWorkspaceId || state.defaultWorkspaceId || '';
  const activeWorkspace = useMemo(
    () => (state.workspaces || []).find((workspace) => workspace.id === activeWorkspaceId) || null,
    [state.workspaces, activeWorkspaceId],
  );
  const creditsRemaining = Number(state.analytics?.creditsRemaining || 0);
  const canManageOrganization = Boolean(state.access?.canManageOrganization);
  const canManageBilling = Boolean(state.access?.canManageBilling);

  const signOut = async () => {
    setAccountAnchor(null);
    await logout();
    navigate('/signin', { replace: true });
  };

  const changeRegion = async () => {
    if (!canSwitchRegion) return;
    if (chooseRegionAgain) await chooseRegionAgain();
    navigate('/region', { replace: true });
  };

  const goHome = () => {
    if (state.user?.email && !onSigninPage && !onRegionPage) navigate('/home');
    else navigate('/signin');
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="transparent"
      sx={{
        bgcolor: 'rgba(249,248,244,.96)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid rgba(26,29,49,.09)',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" gap={1.4} sx={{ minHeight: 68 }}>
          <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', flex: '0 0 auto' }} onClick={goHome}>
            <Brand />
          </Box>

          {showAccountActions && (
            <>
              <Divider orientation="vertical" flexItem sx={{ my: 1.6, mx: .4 }} />
              <Select
                size="small"
                value={activeWorkspaceId}
                onChange={(event) => setActiveWorkspace(event.target.value)}
                IconComponent={KeyboardArrowDownRoundedIcon}
                renderValue={(value) => {
                  const workspace = (state.workspaces || []).find((item) => item.id === value) || activeWorkspace;
                  return (
                    <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                      <Avatar sx={{ width: 26, height: 26, fontSize: 12, fontWeight: 900, bgcolor: '#101228' }}>
                        {initials(workspace?.name || 'Workspace')}
                      </Avatar>
                      <Box minWidth={0}>
                        <Typography sx={{ fontSize: 12.5, lineHeight: 1, color: 'text.secondary' }}>Workspace</Typography>
                        <Typography noWrap sx={{ fontSize: 14, lineHeight: 1.25, fontWeight: 850, maxWidth: 170 }}>
                          {workspace?.name || 'Default'}
                        </Typography>
                      </Box>
                    </Stack>
                  );
                }}
                sx={{
                  minWidth: { xs: 155, sm: 210 },
                  height: 46,
                  bgcolor: '#fff',
                  borderRadius: 1.7,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(26,29,49,.12)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(26,29,49,.25)' },
                  '& .MuiSelect-select': { py: .65, pl: 1 },
                }}
                MenuProps={{ PaperProps: { sx: { mt: .6, minWidth: 250 } } }}
              >
                {(state.workspaces || []).map((workspace) => (
                  <MenuItem key={workspace.id} value={workspace.id}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 900, bgcolor: '#101228' }}>{initials(workspace.name)}</Avatar>
                      <Box>
                        <Typography fontWeight={800}>{workspace.name}</Typography>
                        {workspace.isDefault && <Typography variant="caption" color="text.secondary">Default workspace</Typography>}
                      </Box>
                    </Stack>
                  </MenuItem>
                ))}
                {canManageOrganization && <Divider />}
                {canManageOrganization && <MenuItem onClick={() => navigate('/settings?section=organization')}>All workspaces</MenuItem>}
                {canManageOrganization && <MenuItem onClick={() => navigate('/settings?section=all-mailboxes')}>All mailboxes</MenuItem>}
                {canManageOrganization && <MenuItem onClick={() => navigate('/settings?section=members')}>Team members</MenuItem>}
              </Select>
            </>
          )}

          {showAccountActions && (
            <Stack direction="row" spacing={.5} sx={{ ml: { xs: 'auto', md: 1 } }}>
              <Button
                color="inherit"
                size="small"
                startIcon={<HomeRoundedIcon />}
                onClick={() => navigate('/home')}
                sx={{ display: { xs: 'none', md: 'inline-flex' }, fontWeight: 800, bgcolor: pathname === '/home' ? 'rgba(26,29,49,.055)' : 'transparent' }}
              >
                Home
              </Button>
              <Button
                color="inherit"
                size="small"
                startIcon={<SettingsRoundedIcon />}
                onClick={() => navigate('/settings')}
                sx={{ display: { xs: 'none', md: 'inline-flex' }, fontWeight: 800, bgcolor: pathname.startsWith('/settings') ? 'rgba(26,29,49,.055)' : 'transparent' }}
              >
                Settings
              </Button>
            </Stack>
          )}

          <Box sx={{ flex: 1 }} />

          {showAccountActions && canManageBilling && (
            <Tooltip title="Credits & referrals">
              <Button
                color="inherit"
                size="small"
                startIcon={<AccountBalanceWalletOutlinedIcon />}
                onClick={() => navigate('/settings?section=credits')}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  px: 1.2,
                  bgcolor: 'rgba(255,182,72,.10)',
                  border: '1px solid rgba(255,182,72,.24)',
                  fontWeight: 850,
                }}
              >
                {new Intl.NumberFormat().format(creditsRemaining)} credits
              </Button>
            </Tooltip>
          )}

          <Box
            component={canSwitchRegion ? 'button' : 'div'}
            type={canSwitchRegion ? 'button' : undefined}
            onClick={canSwitchRegion ? changeRegion : undefined}
            sx={{
              appearance: 'none',
              font: 'inherit',
              px: 1.05,
              py: .55,
              borderRadius: 1.2,
              color: 'text.secondary',
              bgcolor: 'rgba(26,29,49,.035)',
              border: '1px solid rgba(26,29,49,.07)',
              cursor: canSwitchRegion ? 'pointer' : 'default',
              display: { xs: 'none', sm: 'inline-flex' },
              alignItems: 'center',
              gap: .6,
            }}
          >
            <PublicRoundedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight={850}>{regionShortLabel(runtimeRegion)}</Typography>
          </Box>

          {showAccountActions && (
            <>
              <IconButton onClick={(event) => setAccountAnchor(event.currentTarget)} sx={{ ml: .3 }}>
                <Avatar sx={{ width: 34, height: 34, bgcolor: '#101228', fontWeight: 900, fontSize: 14 }}>
                  {initials(state.user?.displayName || state.user?.email)}
                </Avatar>
              </IconButton>
              <Menu anchorEl={accountAnchor} open={Boolean(accountAnchor)} onClose={() => setAccountAnchor(null)} PaperProps={{ sx: { minWidth: 240, mt: .8 } }}>
                <Box sx={{ px: 2, py: 1.2 }}>
                  <Typography fontWeight={850}>{state.user?.displayName || 'NudgeForMe user'}</Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>{state.user?.email}</Typography>
                  <Typography variant="caption" color="text.secondary">{state.access?.role === 'workspace_manager' ? 'Workspace Manager' : state.access?.role === 'admin' ? 'Admin' : 'Owner'}</Typography>
                </Box>
                <Divider />
                <MenuItem onClick={() => { setAccountAnchor(null); navigate('/settings'); }}><SettingsRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />Workspace settings</MenuItem>
                {canManageOrganization && <MenuItem onClick={() => { setAccountAnchor(null); navigate('/settings?section=organization'); }}><AdminPanelSettingsRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />Organization settings</MenuItem>}
                <MenuItem onClick={signOut}><LogoutRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />Sign out</MenuItem>
              </Menu>
            </>
          )}
        </Stack>
      </Container>
    </AppBar>
  );
}
